import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setHasActiveCancerDiagnosis } from "store/reducers/user/UserAccountSlice";
import SurveyWrapper from "components/v3/sharedComponents/surveys/SurveyWrapper";
import { PATHS } from "constants/pathConstants";
import { useState } from "react";
import { Box } from "@mui/material";
import {
  getStandaloneSurveyRootSx,
  getStandaloneSurveySlotSx,
  STANDALONE_SURVEY_SURFACE_CLASS
} from "components/v3/layouts/StandaloneSurveyPageLayout";
import SurveySummary from "../SurveySummary";
import {
  deriveSurveyBlockers,
  surveyOutcomeFromBlockers,
  syncMixpanelProfile,
  trackSurveyAbandonAttempted,
  trackSurveyCompleted
} from "analytics";
import { Model } from "survey-core";

const SURVEY_ID = "detailed-cancer-survey";
const WORKFLOW = "cancer_survey" as const;

const CancerSurvey = (): React.ReactElement => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [summarySurveyModel, setSummarySurveyModel] = useState<Model | null>(
    null
  );

  const syncProfileAfterSurvey = (hasActiveCancer: boolean): void => {
    dispatch(setHasActiveCancerDiagnosis(hasActiveCancer));
    syncMixpanelProfile({ hasActiveCancerDiagnosis: hasActiveCancer });
  };

  const handleOnConfirm = (): void => {
    if (!summarySurveyModel) return;

    const blockers = deriveSurveyBlockers(summarySurveyModel);
    const outcome = surveyOutcomeFromBlockers(blockers);

    trackSurveyCompleted({
      surveyId: SURVEY_ID,
      workflow: WORKFLOW,
      outcome,
      blockers
    });

    const hasActiveCancer =
      blockers.includes("active_cancer") ||
      blockers.includes("uncertain_cancer_status");
    syncProfileAfterSurvey(
      blockers.length === 0 ? false : hasActiveCancer
    );

    setShowSummary(false);
    summarySurveyModel.doComplete();
  };

  const handleOnCompleting = (
    surveyModel: Model,
    options: { allow: boolean }
  ): void => {
    if (!showSummary) {
      options.allow = false;
      setSummarySurveyModel(surveyModel);
      setShowSummary(true);
    }
  };

  const handleOnComplete = (): void => {
    history.push(PATHS.LANDING_PAGE);
  };

  const handleOnSurveyInstantiated = (surveyModel: Model): void => {
    surveyModel.pagePrevText = "Back";
  };

  return (
    <Box sx={getStandaloneSurveyRootSx(showSummary)}>
      {showSummary && summarySurveyModel && (
        <Box
          className={STANDALONE_SURVEY_SURFACE_CLASS}
          sx={{
            borderRadius: "16px",
            padding: {
              xs: "16px",
              md: "32px 64px 0 64px"
            }
          }}
        >
          <SurveySummary
            onBack={() => {
              trackSurveyAbandonAttempted({
                surveyId: SURVEY_ID,
                workflow: WORKFLOW,
                action: "cancelled_exit"
              });
              setShowSummary(false);
            }}
            onSubmit={handleOnConfirm}
            surveyModel={summarySurveyModel}
          />
        </Box>
      )}
      <Box sx={getStandaloneSurveySlotSx(showSummary)}>
        <SurveyWrapper
          containerClassName="detailed-cancer-survey-container"
          fillViewport
          onSurveyInstantiated={handleOnSurveyInstantiated}
          onComplete={handleOnComplete}
          onCompleting={handleOnCompleting}
          overrideNextButtonEnabled={true}
          surveyId={SURVEY_ID}
          trackCompletion={false}
        />
      </Box>
    </Box>
  );
};

export default CancerSurvey;
